import { openDB, type IDBPDatabase } from 'idb';
import { CURRENT_WIRE_VERSION } from './replay-headers';

/**
 * 036 T030 (data-model.md "Device") — ONE INDEXEDDB DATABASE PER PRACTITIONER, opened through
 * `idb`. This is the whole of FR-045 on the client: a different practitioner's code asks for a
 * different database name and simply has no way to name this one's rows. *Prefer unrepresentable
 * over refused* (014, 030; research R-002) — there is no filter here to forget.
 *
 * Schema version 1, three object stores: `outbox` (what this file mostly exists for), `views` and
 * `meta` — created now, EMPTY, for a later cycle to fill (data-model.md). Nothing here reads or
 * writes either of the other two; the point of creating them in this build's `upgrade` is that a
 * later build's `upgrade` never has to run at version 1 again to add them.
 */

export const SCHEMA_VERSION = 1;

/**
 * 036 (issue 207) — the one route this build's `submitCapture()` sends.
 * Defined here, not in `outbox.store.ts`, because `isKnownRecord` below needs it too (a stored row
 * naming any other method or path is not something THIS BUILD would have written, whatever the
 * schema otherwise allows). Exported for `outbox.store.ts`'s own use, which is
 * inside `core/outbox/` and therefore not the export `only-door.spec.ts` polices — a production
 * file OUTSIDE this directory naming this identifier, or the literal path, is exactly what that
 * guard exists to catch.
 */
export const CAPTURE_PATH = '/api/v1/actions/quick';

/**
 * 036 T089 (issue 239) — the one route a queued CHANGE sends: `PATCH /api/v1/actions/{actionId}`
 * (`contracts/replayable-writes.md`). `CHANGE_PATH_PREFIX` is exported only so `outbox.store.ts`
 * and this file's own specs can build and check the exact path a change carries — never for a
 * production file outside `core/outbox/` to build a request from directly. `only-door.spec.ts` polices
 * that boundary for this identifier the same way it already does for `CAPTURE_PATH`.
 */
export const CHANGE_METHOD = 'PATCH';
export const CHANGE_PATH_PREFIX = '/api/v1/actions/';

/** The exact path a change to `actionId` is sent to. One place states the pattern (FR-030). */
export function changePath(actionId: number): string {
  return `${CHANGE_PATH_PREFIX}${actionId}`;
}

/**
 * 036 T115 (issue 246) — the one route a queued TAGS change sends: `PUT
 * /api/v1/actions/{actionId}/tags` (`contracts/replayable-writes.md`). `TAGS_PATH_SUFFIX` is
 * exported only so `outbox.store.ts` and this file's own specs can build and check the exact path a
 * tags change carries — never for a production file outside `core/outbox/` to build a request from
 * directly. `only-door.spec.ts` polices that boundary for this identifier the same way it already
 * does for `CAPTURE_PATH` and `CHANGE_PATH_PREFIX`. Unlike those two, the literal text this route
 * adds (`/tags`) is not itself forbidden — `shared/actions/ui/tag-editor.tsx` already builds this
 * exact URL by hand today (not yet migrated onto `submitTagsChange()` — a later slice's own task),
 * so a literal-text guard would be red from the day this identifier was added, for code this slice
 * never touched. The identifier is the one thing a legitimate direct caller has no reason to import.
 */
export const TAGS_METHOD = 'PUT';
export const TAGS_PATH_SUFFIX = '/tags';

/** The exact path a tags change to `actionId` is sent to. One place states the pattern (FR-030). */
export function tagsPath(actionId: number): string {
  return `${changePath(actionId)}${TAGS_PATH_SUFFIX}`;
}

/**
 * Every outbox row's `key` (its idempotency key) used to be `crypto.randomUUID()`
 * directly, at both call sites (`submitCapture`, `replaceCapture`). `crypto.randomUUID` requires a
 * SECURE CONTEXT (HTTPS, or localhost) and is simply `undefined` anywhere else — the compose stack
 * reached by a LAN address, for one — so a capture there threw before it ever reached the device.
 * `crypto.getRandomValues` exists in every context `crypto` itself does (it needs no secure-context
 * gate), so this falls back to building a version 4 UUID from it by hand: RFC 4122 §4.4, the same
 * algorithm `randomUUID()` itself implements, chosen so a key from either path is indistinguishable
 * from the other to every reader of one (the server's idempotency check included).
 */
export function randomKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant 10xx (RFC 4122)
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export type OutboxKind = 'capture' | 'change';

/**
 * A record's own state — deliberately WITHOUT `'unreadable'`. That fifth value belongs only to
 * `UnreadableOutboxRow` below, whose whole purpose is to hold a row this build could not parse as
 * an `OutboxRecord` at all; no code here ever constructs a well-formed record already in that
 * state. Keeping it off `OutboxRecord['state']` is what makes `OutboxRow = OutboxRecord |
 * UnreadableOutboxRow` a real discriminated union — a switch on `row.state` narrows correctly,
 * where a `state` field shared between both members would not.
 */
export type OutboxState = 'waiting' | 'refused' | 'conflict' | 'gone';

export interface OutboxRequest {
  readonly method: string;
  readonly path: string;
  readonly body: unknown;
}

export interface OutboxProblem {
  readonly code: string | null;
  readonly title: string;
  readonly detail: string | null;
}

/**
 * A row this build wrote, or one an earlier build of the SAME wire version wrote — the shape
 * `core/outbox/` reasons about everywhere except the one place that has to admit it might be
 * looking at something else (`toOutboxRow` below).
 *
 * `actionId` and `shows` are `change`-only (data-model.md); this pull request never writes a
 * `change` item, so they are typed but nothing here constructs one.
 */
export interface OutboxRecord {
  readonly seq: number;
  readonly key: string;
  readonly wireVersion: number;
  readonly kind: OutboxKind;
  readonly madeAt: string;
  readonly state: OutboxState;
  readonly request: OutboxRequest;
  readonly actionId?: number;
  readonly shows?: Readonly<Record<string, unknown>>;
  /**
   * 036 (issue 246, FR-030) — a field edit's own base: the value the practitioner was looking at
   * when they changed it, one key per changed field (the client sends one field per item, so this
   * is one key in practice — `contracts/replayable-writes.md`'s own PATCH shape). Present only for
   * a `change` item whose route is the field-edit PATCH; absent for a tags change, which carries
   * `baseNames` instead, and absent when `base` was never supplied (a field that cannot conflict —
   * FR-031 — or one with nothing yet to compare against).
   */
  readonly base?: Readonly<Record<string, unknown>>;
  /** 036 (issue 246, FR-030) — the tag set the practitioner was looking at when they changed it.
   *  Present only for a `change` item whose route is the tags PUT. */
  readonly baseNames?: readonly string[];
  /**
   * 036 (issue 246, FR-033) — learned once, on a `409 action.edit_conflict`, by reading the action
   * back: the server's current value for whatever `base` (or `baseNames`, as `{ names: [...] }`)
   * named. Present only once `state` is `conflict`, and never inspected for domain meaning here —
   * only carried, the same as `shows`.
   */
  readonly theirs?: Readonly<Record<string, unknown>>;
  readonly problem?: OutboxProblem;
  /**
   * issue 208's bad-row rule: how many strikes this row has. A strike is a `5xx` or a malformed
   * `2xx` on this row that came AFTER some other request to the API had succeeded, so the server
   * was shown to be working; a failure without that evidence is not counted. Absent (read as `0`)
   * for a row with
   * none — every row this build ever queues starts this way, and `replaceCapture` (an edit, "send
   * again") writes a fresh record that omits it, which is how sending again clears the count.
   */
  readonly strikes?: number;
  /**
   * The evidence counter's value (`core/reach/reach.store.ts`'s `evidenceCount()`) at the moment
   * this row was queued, or at its last strike — what a candidate failure is compared against to
   * decide whether the SERVER has shown a sign of life, from a request other than this row's own,
   * since then. Never meaningful across a reload: `evidenceCount()` restarts at zero there, so a
   * stored value greater than the live counter is what a reload looks like, and is read as "no
   * evidence yet" rather than as a claim about a session that no longer exists.
   */
  readonly evidenceMark?: number;
}

/** What `addCapture` is given: everything but the `seq` the store assigns on write. */
export type NewOutboxRecord = Omit<OutboxRecord, 'seq'>;

/**
 * FR-056: "An item [a build] cannot read MUST be shown as unreadable, with its text, and MUST NOT
 * be dropped." `text` is the best this build can do at recovering something to show — the line of
 * a capture, when the stored shape has one readable that far, or an empty string otherwise. It is
 * never invented from nothing.
 */
export interface UnreadableOutboxRow {
  readonly seq: number;
  readonly state: 'unreadable';
  readonly text: string;
}

export type OutboxRow = OutboxRecord | UnreadableOutboxRow;

export const isUnreadableRow = (row: OutboxRow): row is UnreadableOutboxRow => row.state === 'unreadable';

const KNOWN_KINDS: readonly OutboxKind[] = ['capture', 'change'];
const KNOWN_STATES: readonly OutboxState[] = ['waiting', 'refused', 'conflict', 'gone'];

/**
 * The validation FR-056 asks for. Deliberately narrow: this checks the SHAPE this build knows how
 * to send and to draw, not whether the values inside it make domain sense — that is every other
 * module's job, and this one must not grow an opinion about a capture's line (FR-016, SC-014).
 *
 * An unrecognised `wireVersion` fails here on purpose, even though every other field might be
 * perfectly readable: `wireVersion` states the format this item was WRITTEN in, and a version this
 * build does not recognise is exactly what FR-056 is for, whatever the rest of the row looks like.
 *
 * **This row is sendable only if it is what this build would have stored.** A row
 * naming a `method`/`path` other than one of the two routes `submitCapture()`/`submitChange()`
 * ever write is data from the DEVICE, and a device is not trusted for where its rows say they go:
 * something a browser extension or a hand-edited IndexedDB session put there, or simple
 * corruption, must never be dispatched as if this build had chosen its destination. Such a row
 * fails here — exactly like an unrecognised `wireVersion` — and becomes `UnreadableOutboxRow`:
 * shown, never sent, never dropped (FR-056's own guarantee, extended to "wrong destination" as
 * well as "wrong shape").
 *
 * **036 T089 — a `change` is trusted only when its own fields agree with each other.** `actionId`
 * must be a number, `shows` a plain object (what the row draws — FR-028/FR-030, never inspected
 * for domain meaning here, only for shape), the method `PATCH`, and — the same distrust the
 * capture branch already applies to its route — the `path` must name the SAME `actionId` the row
 * itself carries. A row whose path and actionId disagree (tampered, tabbed with by another build,
 * or corrupted) is not one this build chose the destination of, and becomes unreadable rather than
 * sent to whichever id the mismatch happens to point at.
 *
 * **036 (issue 246) — a change item is trusted as one of exactly two shapes, distinguished by
 * `baseNames`'s presence, never by anything the row merely claims about itself.** A tags change
 * (`baseNames` present) must carry an array of strings and a `PUT` to the tags route; a field-edit
 * change (no `baseNames`) must carry a `PATCH` to the plain route, and a `base` — when present —
 * must be a plain object, the same shape-only check `shows` already gets (never a domain check on
 * what it contains, exactly as FR-016/SC-014 requires). A row in `conflict` must carry a `theirs` of
 * the same plausible shape, or it is not a conflict this build produced and reads as unreadable
 * rather than being drawn as one.
 */
function isKnownRecord(raw: unknown): raw is Omit<OutboxRecord, 'seq'> {
  if (!raw || typeof raw !== 'object') return false;
  const r = raw as Record<string, unknown>;
  if (typeof r['key'] !== 'string') return false;
  if (r['wireVersion'] !== CURRENT_WIRE_VERSION) return false;
  if (typeof r['kind'] !== 'string' || !KNOWN_KINDS.includes(r['kind'] as OutboxKind)) return false;
  const kind = r['kind'] as OutboxKind;
  if (typeof r['madeAt'] !== 'string') return false;
  if (typeof r['state'] !== 'string' || !KNOWN_STATES.includes(r['state'] as OutboxState)) return false;
  const request = r['request'];
  if (!request || typeof request !== 'object') return false;
  const req = request as Record<string, unknown>;

  if (kind === 'capture') {
    if (req['method'] !== 'POST') return false; // this build sends captures only to the one route
    if (req['path'] !== CAPTURE_PATH) return false;
    return true;
  }

  // `kind === 'change'` (KNOWN_KINDS has no third value).
  if (typeof r['actionId'] !== 'number') return false;
  const shows = r['shows'];
  if (!shows || typeof shows !== 'object' || Array.isArray(shows)) return false;
  const actionId = r['actionId'];

  if ('baseNames' in r) {
    const baseNames = r['baseNames'];
    if (!Array.isArray(baseNames) || !baseNames.every((name) => typeof name === 'string')) return false;
    if (req['method'] !== TAGS_METHOD) return false;
    if (req['path'] !== tagsPath(actionId)) return false; // must name ITS OWN actionId
  } else {
    if (req['method'] !== CHANGE_METHOD) return false;
    if (req['path'] !== changePath(actionId)) return false; // must name ITS OWN actionId
    if ('base' in r) {
      const base = r['base'];
      if (!base || typeof base !== 'object' || Array.isArray(base)) return false;
    }
  }

  if (r['state'] === 'conflict') {
    const theirs = r['theirs'];
    if (!theirs || typeof theirs !== 'object' || Array.isArray(theirs)) return false;
  }

  return true;
}

/** The most this build can recover from a row it does not otherwise recognise (FR-056). */
function bestEffortText(raw: unknown): string {
  if (!raw || typeof raw !== 'object') return '';
  const request = (raw as Record<string, unknown>)['request'];
  if (request && typeof request === 'object') {
    const body = (request as Record<string, unknown>)['body'];
    if (body && typeof body === 'object') {
      const line = (body as Record<string, unknown>)['line'];
      if (typeof line === 'string') return line;
    }
  }
  return '';
}

/** Exported for `outbox.store.ts`'s "no fields ever go stale" mapping and for this file's specs. */
export function toOutboxRow(seq: number, raw: unknown): OutboxRow {
  if (isKnownRecord(raw)) return { ...raw, seq };
  return { seq, state: 'unreadable', text: bestEffortText(raw) };
}

/** `data-model.md`: "named `completeme.offline.<practitionerId>`." One place states the pattern. */
export function databaseName(practitionerId: number | string): string {
  return `completeme.offline.${practitionerId}`;
}

/**
 * Issue 280 — every practitioner with a device database on THIS device, read from the browser's own
 * list. For the one caller that has to act on a practitioner it cannot name: a session refused with
 * no account in memory (`session.store.ts`'s `clear()`).
 *
 * The prefix is `databaseName('')` and not a second copy of the pattern, so the two cannot drift.
 * An engine without `indexedDB.databases` answers an empty list — the caller is ending a session and
 * must not fail on it — and says so once, since "nobody is here" and "I could not look" are
 * different facts.
 */
export async function deviceDatabaseOwners(): Promise<string[]> {
  if (typeof indexedDB.databases !== 'function') {
    console.warn('device-database: this browser cannot list its databases; no owners found');
    return [];
  }
  const prefix = databaseName('');
  const owners: string[] = [];
  for (const { name } of await indexedDB.databases()) {
    if (name && name.startsWith(prefix) && name.length > prefix.length) owners.push(name.slice(prefix.length));
  }
  return owners;
}

/**
 * 036 (issue 208, research R-008) — one row of the `views` object store: the server's answer to one
 * `GET`, unaltered (FR-019), the moment it said so (FR-018, its own `Date` header), and the build
 * that wrote it. Keyed by `url` — the exact request, query string included, since keying any other
 * way would mean deciding a context page IS home's data reshaped, which R-008 rejects outright.
 *
 * No token and no credential is ever a member here (FR-048) — `body` is the parsed response of a
 * `GET` this build already trusts to carry none, exactly as it never has anywhere else this
 * application keeps a server's answer.
 */
export interface StoredView {
  readonly url: string;
  readonly body: unknown;
  /** The server's own `Date` response header, verbatim — an HTTP-date string, not reduced to a
   *  day or re-encoded, so `shared/actions/model/dates.ts` is still the one place that formats it. */
  readonly date: string;
  /** `environments/build-id.ts`'s `BUILD_ID` at the moment this was written. A view written by a
   *  different build is treated as absent (research R-008's "one build's shape is not another's"). */
  readonly buildId: string;
}

export interface DeviceDatabase {
  /** Escape hatch for a spec that needs to reach past this wrapper (see `device-database.spec.ts`'s
   *  unreadable-row cases, which write a row this module's own API has no way to construct). */
  readonly db: IDBPDatabase;
  /**
   * True once this connection has been closed, by ITS OWNER calling
   * `close()` or by the browser calling this connection's own `blocking` handler because ANOTHER
   * connection (another tab, or a later open in this same one) asked it to step aside. A caller
   * that caches a `DeviceDatabase` MUST check this after every `await` and never reuse a handle
   * once it reads `true` — every method below throws `InvalidStateError` on a closed connection,
   * which is what "a closed handle is never cached" (point 10) exists to make unreachable.
   */
  readonly closed: boolean;
  /** Returns the `seq` the store assigned. The only way a row enters `outbox` in this pull request. */
  addCapture(record: NewOutboxRecord): Promise<number>;
  updateState(seq: number, patch: Partial<Pick<OutboxRecord, 'state' | 'problem'>>): Promise<void>;
  /**
   * An edit, as ONE read-then-write transaction: reads the row at `seq`,
   * and if it is still there and still readable, writes it back under the SAME `seq` with `body`
   * replacing the old one, a NEW `key` (an edited line is something the practitioner said now, not
   * a replay of what they said before), `state: 'waiting'`, no `problem`, and `madeAt` of `now`.
   * Resolves `false` — refuses — when the row is gone (already sent, already discarded, or never
   * existed) rather than throwing; a discard or a send landing between this call being made and it
   * running is an ordinary race, not an exceptional one. Never used to change `method`/`path`/`kind`
   * — an edit is still a capture to the same route it always was.
   *
   * `evidenceMark` re-bases the bad-row rule's own count for the FRESH row this writes: an edit
   * ("send again") clears `strikes` to none (the replacement object simply omits it) and restarts
   * the "since it was queued" clock at the evidence counter's CURRENT value — its caller's, not
   * `0`, since `0` would read every success this session has already seen as evidence the fresh row
   * never actually waited through. Optional only so a caller not exercising the bad-row rule (this
   * file's own specs included) need not supply one; production always does
   * (`core/outbox/outbox.store.ts`'s own `replaceCapture`).
   */
  replaceCapture(seq: number, body: unknown, now: string, evidenceMark?: number): Promise<boolean>;
  deleteItem(seq: number): Promise<void>;
  /**
   * 036 (issue 257, A4) — "keep mine": the conflicted row at `seq` is replaced by a FRESH row
   * (`record`, built by the caller — the practitioner's own value, `base`/`baseNames` now the
   * server's), as ONE transaction. The previous shape (`deleteItem` then `addCapture`, two separate
   * transactions each committing on its own) could lose the practitioner's value outright: if the add
   * failed — a full disk, a blocked device — AFTER the delete had already committed, the conflicted
   * row was gone and nothing had replaced it, breaking FR-056's own "never dropped" promise for
   * exactly the row this screen exists to protect. One transaction makes the two halves atomic: a
   * failure here throws (the caller decides what a device that will not store anything means, exactly
   * as `replaceCapture`'s own caller does) and leaves the ORIGINAL conflicted row exactly as it was —
   * IndexedDB aborts every write a failed transaction attempted, the delete included.
   *
   * Resolves the new row's own `seq`, or `null` when `seq` no longer names a row to replace (moved on
   * before this ran — already withdrawn, discarded, or answered some other way) — the same "refuses
   * rather than throws for an ordinary race" rule `replaceCapture` already follows.
   */
  replaceWithFreshRecord(seq: number, record: NewOutboxRecord): Promise<number | null>;
  /**
   * An INDEPENDENT guard alongside the send loop's own key check — "an answer acts on a row only if
   * the row still carries the key that was sent." One transaction: reads the row at `seq`, and
   * deletes it ONLY if its `key` still matches `key`. A row is edited in place at the SAME `seq`
   * under a NEW `key` (`replaceCapture`, above); a delete meant for the ORIGINAL submission must
   * never remove the EDITED one just because it happens to sit at the same `seq` — that would
   * silently lose an edit: the server holds the new text, and a stale answer for the old submission
   * would otherwise delete the row that carries it. Returns whether it actually deleted anything.
   */
  deleteIfKey(seq: number, key: string): Promise<boolean>;
  /** The same guard, for a refusal rather than an answer — see `deleteIfKey`. Returns whether it
   *  actually wrote the refusal. */
  updateStateIfKey(
    seq: number,
    key: string,
    patch: Partial<Pick<OutboxRecord, 'state' | 'problem' | 'strikes' | 'evidenceMark' | 'theirs'>>,
  ): Promise<boolean>;
  /**
   * The outbox always keeps a refused row; a STORE that can still show the refusal (same screen,
   * same container or tag) is what decides to remove it, once it has. ONE transaction: reads the
   * row at `seq`, and deletes it ONLY if it is STILL `refused` — a row resent (`replaceCapture`)
   * since the refusal was shown has moved on to a NEW key and a fresh attempt, and a dismiss aimed
   * at the refusal the store already displayed must not remove that. Returns whether it actually
   * deleted anything.
   */
  deleteIfRefused(seq: number): Promise<boolean>;
  /** Every row, in `seq` order — the order IndexedDB's own key order already gives for free, which
   *  is what makes "ordered by seq and nothing else" (FR-057) a structural property rather than a
   *  sort this file could get wrong. */
  allRows(): Promise<OutboxRow[]>;
  /** One row, read fresh, immediately before the send loop trusts it. `allRows()`'s
   *  answer is a snapshot; this is what tells the loop a row it saw a moment ago is gone or has
   *  changed since. `null` when the row no longer exists. */
  getRow(seq: number): Promise<OutboxRow | null>;
  /** 036 (issue 208) — the `views` store's own three operations, alongside the outbox's. Writes or
   *  replaces the row keyed by `view.url` (R-008: an answer already stored under that URL is
   *  replaced by the fresher one, never merged with it — FR-019's "unaltered" applies to what is
   *  KEPT, not to reconciling two answers). */
  putView(view: StoredView): Promise<void>;
  /** `null` when nothing is stored for this exact URL. Never validates `buildId` itself — that
   *  comparison is `core/stored-views/`'s (research R-008: "a view stored by another build is
   *  treated as absent"), so this file stays ignorant of what a build id even means. */
  getView(url: string): Promise<StoredView | null>;
  /** FR-046 — every stored view, deleted, on a sign-out or a refusal of the session. Leaves
   *  `outbox` and `meta` untouched: unsent items survive exactly that (FR-050). */
  clearViews(): Promise<void>;
  /**
   * 036 (issue 222, FR-023) — the `meta` store's own use, alongside `outbox` and `views`: when a
   * context, project or tag PAGE was last opened on this device, so fetch-ahead can prefetch the
   * ones most recently opened first (`core/stored-views/fetch-ahead.ts` is the one reader; this
   * file owns only how the log is kept). `key` is `core/stored-views/last-opened.ts`'s own
   * `context:<id>` / `project:<id>` / `tag:<name>`; this module has no opinion about its shape.
   *
   * One JSON document under one fixed key (`'openedLog'`), read-modified-written under a single
   * transaction, rather than one row per page: the whole log is at most a few hundred entries (an
   * account with two hundred containers, SC-016's own scale) and is read as a WHOLE every fetch-ahead
   * run anyway (`fetch-ahead.ts` needs every entry to sort by), so a cursor over one row per key would
   * cost more round trips for no reader that ever wants a single entry alone.
   *
   * Survives a sign-out untouched, same as `outbox` — only `views` is FR-046's own subject.
   */
  recordOpened(key: string, when: string): Promise<void>;
  /** Every entry `recordOpened` has ever written, or `{}` when nothing has been opened yet. */
  openedLog(): Promise<Readonly<Record<string, string>>>;
  close(): void;
}

/**
 * One open IN FLIGHT per database name. `outbox.store.ts`'s `databaseFor` already
 * caches the RESOLVED handle across calls; this closes the narrower gap where two callers both see
 * no handle yet and both start opening (`layout.tsx`'s mount effect and `/unsent`'s racing on
 * startup) — without it, whichever resolves second overwrites the reference to whichever resolved
 * first, and the first is never closed.
 *
 * Entries are removed once the open settles, success or failure: this is a de-duplication of
 * concurrent opens, not a permanent cache of resolved connections — the resolved connection can
 * later be closed by its owner (`databaseFor`, `discardOutboxFor`), and a stale entry here would
 * hand back a closed connection to a caller with no way to know.
 */
const openings = new Map<string, Promise<DeviceDatabase>>();

export function openDeviceDatabase(practitionerId: number | string): Promise<DeviceDatabase> {
  const name = databaseName(practitionerId);
  const existing = openings.get(name);
  if (existing) return existing;

  const opening = openDeviceDatabaseNow(name).finally(() => {
    openings.delete(name);
  });
  openings.set(name, opening);
  return opening;
}

/**
 * 036 (issue 208) — for a caller that opens, does exactly one thing, and closes again immediately
 * (`core/stored-views/device-views.ts`'s three functions), never `openDeviceDatabase` above.
 *
 * **Why a separate entry point rather than the shared one.** `openDeviceDatabase`'s de-duplication
 * hands out the SAME connection object to every caller racing for the same name while one open is
 * still in flight — correct for `databaseFor`'s own two mount points (both long-lived, both meaning
 * to keep whatever they get), and wrong the moment a one-shot caller is in that race too: a one-shot
 * caller closes what it gets back the instant its own operation finishes, and a shared connection
 * closed that way is closed out from under whichever OTHER caller — `databaseFor`'s own long-lived
 * handle among them — the de-dup handed the identical object to. Found by
 * `session.store.unconfirmed.spec.ts`'s own criterion 5 going red only in file order, never alone: a
 * prior test's sign-out fires `clearStoredViewsFor` fire-and-forget (`session.store.ts`'s own
 * comment on why that call is never awaited), and when the next test's `submitCapture` opened the
 * outbox's connection while that stray call was still in flight for the same practitioner, the two
 * shared one object — and the stray call's own `close()` then closed the outbox's connection before
 * it had sent anything, so the write silently went nowhere the outbox itself could still use.
 *
 * A fresh, exclusively-owned connection is not a race with `databaseFor`'s at all: IndexedDB allows
 * more than one open connection to the same database at the same version at once, so nothing here
 * needs to wait for or share what `databaseFor` is holding — it only needs to never be handed to a
 * second caller who might close it first.
 */
export function openDeviceDatabaseExclusive(practitionerId: number | string): Promise<DeviceDatabase> {
  return openDeviceDatabaseNow(databaseName(practitionerId));
}

async function openDeviceDatabaseNow(name: string): Promise<DeviceDatabase> {
  // the ONE flag every method below checks. Set by `close()` (this
  // wrapper's owner asking to stop) and by `blocking()` (the BROWSER telling this connection to
  // step aside for another one) alike, so a caller that only ever calls this wrapper's own
  // methods — never `db` directly — cannot observe a live-looking handle that is actually dead.
  let closed = false;

  const db = await openDB(name, SCHEMA_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains('outbox')) {
        const outbox = database.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
        outbox.createIndex('byAction', 'actionId');
      }
      if (!database.objectStoreNames.contains('views')) {
        database.createObjectStore('views', { keyPath: 'url' });
      }
      if (!database.objectStoreNames.contains('meta')) {
        database.createObjectStore('meta');
      }
    },
    /**
     * Another connection (another tab, or this one's own later call) wants to
     * delete or upgrade this database and is BLOCKED by this one still being open. Closing lets it
     * proceed instead of leaving `deleteDeviceDatabase` waiting on a connection nothing will ever
     * close otherwise. Confirmed sign-out (FR-049) is exactly this case: the tracked handle closes
     * on its own path, but a SECOND, leaked connection from the race this file's `openings` map
     * exists to prevent would have blocked the delete forever without this.
     *
     * The connection is taken from the event rather than from a variable assigned after `openDB`
     * returns: the event's target IS the connection being asked to step aside, so there is no
     * moment at which this could run and find nothing to close.
     *
     * `closed` is set HERE too, not only in this wrapper's own
     * `close()`. Before this, a connection closed by the BROWSER (rather than by its owner calling
     * `close()`) left `closed` false forever: the owner's cached wrapper looked exactly as healthy
     * as it had a moment before, and every later operation on it threw `InvalidStateError` — which
     * is what made the capture box wrongly claim "This browser will not let CompleteMe keep
     * anything" after a sign-out in another tab deleted the database out from under this one.
     */
    blocking(_currentVersion, _blockedVersion, event) {
      (event.target as IDBDatabase).close();
      closed = true;
    },
  });

  return {
    db,
    get closed() { return closed; },
    async addCapture(record) {
      return (await db.add('outbox', record)) as number;
    },
    async updateState(seq, patch) {
      /**
       * ONE readwrite transaction, not `db.get` then `db.put` (two
       * transactions, each committing on its own). A discard landing in the gap between them found
       * nothing wrong with overwriting a row that had, by then, already been deleted — the delete's
       * own transaction had already committed, and this one's `put` simply re-created the row, so
       * the discarded item reappeared. One transaction removes the gap rather than narrowing it.
       */
      const tx = db.transaction('outbox', 'readwrite');
      const existing = (await tx.store.get(seq)) as OutboxRecord | undefined;
      if (existing) await tx.store.put({ ...existing, ...patch, seq });
      await tx.done;
    },
    async replaceCapture(seq, body, now, evidenceMark) {
      const tx = db.transaction('outbox', 'readwrite');
      const raw = await tx.store.get(seq);
      if (raw === undefined) {
        await tx.done;
        return false;
      }
      const existing = toOutboxRow(seq, raw);
      if (isUnreadableRow(existing)) {
        // Not this build's shape to begin with — replacing it would be inventing a `key` and a
        // `madeAt` for a row this build never wrote and cannot otherwise vouch for.
        await tx.done;
        return false;
      }
      const replaced: OutboxRecord = {
        key: randomKey(),
        wireVersion: existing.wireVersion,
        kind: existing.kind,
        madeAt: now,
        state: 'waiting',
        request: { ...existing.request, body },
        seq: existing.seq,
        actionId: existing.actionId,
        shows: existing.shows,
        // `problem` deliberately omitted: a fresh `waiting` row carries no refusal.
        // `strikes` deliberately omitted too (issue 208): sending again clears the count.
        evidenceMark,
      };
      await tx.store.put(replaced);
      await tx.done;
      return true;
    },
    async deleteItem(seq) {
      await db.delete('outbox', seq);
    },
    async replaceWithFreshRecord(seq, record) {
      const tx = db.transaction('outbox', 'readwrite');
      try {
        const existing = await tx.store.get(seq);
        if (existing === undefined) {
          await tx.done;
          return null;
        }
        await tx.store.delete(seq);
        const newSeq = (await tx.store.add(record)) as number;
        await tx.done;
        return newSeq;
      } catch (error) {
        /**
         * 036 (issue 257, A4) — EXPLICIT abort, not left to the transaction's own auto-commit timing.
         * A value the structured clone algorithm cannot serialise (measured against `fake-indexeddb`
         * while writing this) makes `tx.store.add()` throw SYNCHRONOUSLY, before any `IDBRequest` is
         * ever created for it — which means no request ever errors, which means nothing about this
         * transaction's own lifecycle would otherwise notice anything went wrong. Left alone, the
         * transaction still auto-commits once its (now-empty) request queue drains, carrying the
         * DELETE above with it — exactly the lost-conflicted-row failure this method exists to
         * prevent, just from a different cause than the one its own header comment names. `abort()`
         * is what actually undoes the delete; a plain `throw` here, without it, does not.
         */
        try { tx.abort(); } catch { /* already finished one way or another; nothing more to undo */ }
        /**
         * 036 (issue 257, A4 follow-up) — `abort()` makes `idb`'s own `tx.done` REJECT with an
         * `AbortError`, and nothing above this point ever awaits or catches `tx.done` again on this
         * path. Left alone that is an UNHANDLED rejection — in this test environment and, just as
         * much, in production, every time a `keepMine` add genuinely fails this way (measured: the
         * mutation that proved this method atomic also proved this half of it broken, caught by the
         * coordinator's own full-suite run, not by this file's own targeted one — `npm test`'s
         * PASS COUNT said nothing about it; its EXIT CODE and "Unhandled Errors" section did).
         * Consumed here, deliberately, and ONLY the `AbortError` this exact `abort()` call produces —
         * any other rejection from `tx.done` is a second, genuine failure and must still propagate.
         * The caller sees the ORIGINAL error either way, never this one.
         */
        try {
          await tx.done;
        } catch (abortError) {
          // `.name`, not `instanceof DOMException` — a `DOMException` thrown across an
          // `idb`/`fake-indexeddb` boundary is not reliably `instanceof` every realm's own
          // constructor (measured: this file's own spec needed the identical fix for its assertion).
          const name = (abortError as { name?: unknown } | null)?.name;
          if (name !== 'AbortError') throw abortError;
        }
        throw error;
      }
    },
    async deleteIfRefused(seq) {
      const tx = db.transaction('outbox', 'readwrite');
      const raw = await tx.store.get(seq);
      if (raw === undefined || (raw as OutboxRecord).state !== 'refused') {
        await tx.done;
        return false;
      }
      await tx.store.delete(seq);
      await tx.done;
      return true;
    },
    async deleteIfKey(seq, key) {
      const tx = db.transaction('outbox', 'readwrite');
      const raw = await tx.store.get(seq);
      if (raw === undefined || (raw as OutboxRecord).key !== key) {
        await tx.done;
        return false;
      }
      await tx.store.delete(seq);
      await tx.done;
      return true;
    },
    async updateStateIfKey(seq, key, patch) {
      const tx = db.transaction('outbox', 'readwrite');
      const existing = (await tx.store.get(seq)) as OutboxRecord | undefined;
      if (existing === undefined || existing.key !== key) {
        await tx.done;
        return false;
      }
      await tx.store.put({ ...existing, ...patch, seq });
      await tx.done;
      return true;
    },
    /**
     * ONE transaction, pairing each row's `seq` with its value from the SAME
     * cursor entry. The previous form read `getAllKeys()` and `getAll()` as two separate calls and
     * zipped them by array index; a write landing between them shifted the two arrays against each
     * other, so a row could be paired with the WRONG key — and the send loop would then delete or
     * update whichever record happened to sit at that index once it heard back, not the one it
     * actually sent.
     */
    async allRows() {
      const tx = db.transaction('outbox', 'readonly');
      const rows: OutboxRow[] = [];
      let cursor = await tx.store.openCursor();
      while (cursor) {
        rows.push(toOutboxRow(cursor.key as number, cursor.value));
        cursor = await cursor.continue();
      }
      await tx.done;
      return rows;
    },
    async getRow(seq) {
      const raw = await db.get('outbox', seq);
      if (raw === undefined) return null;
      return toOutboxRow(seq, raw);
    },
    async putView(view) {
      await db.put('views', view);
    },
    async getView(url) {
      const raw = await db.get('views', url);
      return (raw as StoredView | undefined) ?? null;
    },
    async clearViews() {
      await db.clear('views');
    },
    async recordOpened(key, when) {
      const tx = db.transaction('meta', 'readwrite');
      const existing = (await tx.store.get('openedLog')) as Record<string, string> | undefined;
      await tx.store.put({ ...existing, [key]: when }, 'openedLog');
      await tx.done;
    },
    async openedLog() {
      const raw = await db.get('meta', 'openedLog');
      return (raw as Record<string, string> | undefined) ?? {};
    },
    close() {
      db.close();
      closed = true;
    },
  };
}

/**
 * Deletes one practitioner's whole database — signing out with unsent items discarded (FR-049),
 * and a different practitioner's database is simply never opened (FR-045).
 *
 * Resolves only once the database is REALLY gone. It used to resolve on
 * `onblocked` too, on the reasoning that "an open connection elsewhere will close and release it"
 * — which stated an intention this file did nothing to bring about. A connection left open (the
 * leaked one from the race item 8 also fixes, or simply another tab) blocked the delete
 * indefinitely while the caller had already been told it succeeded: "Sign out and delete" would
 * report done while the practitioner's captures were still on the device. Only `onsuccess` counts
 * now; every open connection's own `blocking` handler (above) is what actually lets a blocked
 * delete complete.
 */
export function deleteDeviceDatabase(practitionerId: number | string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(databaseName(practitionerId));
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}
